import { PageIcon } from "@/components/PageIcon";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FolderKanban, Loader2, Plus, AlertTriangle, Ban } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import ProjectForm from "@/components/ProjectForm";

export const STATUS_COLOUR = {
  Planning: "bg-slate-100 text-slate-600 border-slate-200",
  Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "On Hold": "bg-amber-50 text-amber-700 border-amber-200",
  Completed: "bg-indigo-50 text-indigo-700 border-indigo-200",
  Cancelled: "bg-rose-50 text-rose-700 border-rose-200",
};

export function StatusChip({ status }) {
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_COLOUR[status] || STATUS_COLOUR.Planning}`}>{status}</span>;
}

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const canManage = can("projects.manage");
  const [items, setItems] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = () => api.get("/projects").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="page-projects">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <PageIcon />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Projects</h1>
            <p className="text-sm text-slate-500 mt-0.5">Larger jobs that group multiple tasks over a long period</p>
          </div>
        </div>
        {canManage && (
          <Button onClick={() => setShowAdd(true)} className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white shrink-0" data-testid="projects-new-btn">
            <Plus className="w-4 h-4" /> New Project
          </Button>
        )}
      </div>

      {items === null ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> Loading projects…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500" data-testid="projects-empty">
          No projects yet. {canManage && "Create one to group related jobs together."}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="projects-grid">
          {items.map((p) => (
            <button key={p.project_id} onClick={() => navigate(`/projects/${p.project_id}`)}
              className="text-left bg-white border border-slate-200 rounded-2xl shadow-sm p-5 hover:shadow-md hover:border-indigo-200 transition-all" data-testid={`project-card-${p.project_id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <h2 className="text-base font-bold text-slate-900 leading-snug">{p.name}</h2>
                <StatusChip status={p.status} />
              </div>
              <p className="text-sm text-slate-500 mb-1">{p.customer_name || "No customer"}</p>
              <p className="text-xs text-slate-400 mb-3">{[p.site_address?.city, p.site_address?.postcode].filter(Boolean).join(" · ") || "—"}</p>

              <div className="mb-3">
                <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                  <span>{p.task_count} task{p.task_count === 1 ? "" : "s"}</span>
                  <span className="font-semibold text-slate-700">{p.progress_pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${p.progress_pct}%` }} />
                </div>
              </div>

              <div className="flex items-center gap-3 text-xs">
                {p.blocked_count > 0 && <span className="inline-flex items-center gap-1 text-rose-600 font-medium"><Ban className="w-3.5 h-3.5" /> {p.blocked_count} blocked</span>}
                {p.overdue_count > 0 && <span className="inline-flex items-center gap-1 text-amber-600 font-medium"><AlertTriangle className="w-3.5 h-3.5" /> {p.overdue_count} overdue</span>}
                {p.target_end && <span className="text-slate-400 ml-auto">Due {p.target_end}</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      {showAdd && <ProjectForm mode="create" onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}
